/**
 * Input sanitization and validation utilities
 * Helps prevent XSS attacks and ensures data integrity
 */

/**
 * Sanitize string input by removing potentially dangerous characters
 * and limiting length
 */
export const sanitizeString = (
  input: string,
  maxLength: number = 100
): string => {
  if (!input || typeof input !== "string") return "";

  return input
    .trim()
    .replace(/[<>"'&]/g, "") // Remove XSS-prone characters
    .replace(/\s+/g, " ") // Normalize whitespace
    .substring(0, maxLength);
};

/**
 * Validate name fields (allows letters, spaces, hyphens, apostrophes, periods)
 */
export const validateName = (name: string): boolean => {
  if (!name || typeof name !== "string") return false;
  const sanitized = sanitizeString(name, 50);
  return /^[a-zA-Z\s\-'.]{1,50}$/.test(sanitized) && sanitized.length >= 1;
};

/**
 * Filter name input in real-time to only allow valid characters
 * Allows: letters, spaces, hyphens (-), apostrophes ('), periods (.)
 */
export const filterNameInput = (input: string): string => {
  if (!input || typeof input !== "string") return "";
  return input.replace(/[^a-zA-Z\s\-'.]/g, "");
};

/**
 * Validate date string (YYYY-MM-DD format, reasonable date range)
 */
export const validateDate = (date: string): boolean => {
  if (!date) return true; // Optional field

  const dateObj = new Date(date);
  const now = new Date();
  const minDate = new Date("1900-01-01");

  return !isNaN(dateObj.getTime()) && dateObj >= minDate && dateObj <= now;
};

/**
 * Validate preference string (safe content, reasonable length)
 */
export const validatePreference = (preference: string): boolean => {
  if (!preference || typeof preference !== "string") return false;
  const sanitized = sanitizeString(preference, 100);
  return sanitized.length >= 1 && sanitized.length <= 100;
};

/**
 * Sanitize and validate basic profile update data
 */
export const sanitizeBasicProfileData = (data: Record<string, any>) => {
  const sanitized: Record<string, string> = {};

  // Name fields
  if (data.title) sanitized.title = sanitizeString(data.title, 20);
  if (data.firstName) sanitized.firstName = sanitizeString(data.firstName, 50);
  if (data.middleName)
    sanitized.middleName = sanitizeString(data.middleName, 50);
  if (data.lastName) sanitized.lastName = sanitizeString(data.lastName, 50);
  if (data.suffix) sanitized.suffix = sanitizeString(data.suffix, 20);
  if (data.gender) sanitized.gender = sanitizeString(data.gender, 20);

  // Date field
  if (data.dob && validateDate(data.dob)) {
    sanitized.dob = data.dob;
  }

  return sanitized;
};

/**
 * Sanitize preference arrays
 */
export const sanitizePreferences = (preferences: string[]): string[] => {
  if (!Array.isArray(preferences)) return [];

  return preferences
    .map((pref) => sanitizeString(pref, 100))
    .filter((pref) => pref.length > 0)
    .slice(0, 20); // Limit number of preferences
};

/**
 * Validate guest preference update data
 */
export const sanitizeGuestPreferenceData = (data: Record<string, any>) => {
  const sanitized: Record<string, string> = {};

  // Preference fields
  if (data.type && typeof data.type === "string") {
    sanitized.type = sanitizeString(data.type, 50);
  }
  if (data.value && typeof data.value === "string") {
    sanitized.value = sanitizeString(data.value, 100);
  }

  return sanitized;
};
